import React, { Suspense } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Button, Table, Modal } from 'reactstrap';
import { useNavigate } from 'react-router-dom';

import { TableGroupsStyled } from './styles';
import Shimmer from 'components/Shimmer';
import ModalEditGroup from '../Modal-Edit-Group';
import styled from 'styled-components';
import axios from 'axios';
import { useAuth } from 'hooks/useAuth';
import { getFirebaseBackend } from 'helpers/firebaseHelper';

const TableGroups = ({ groups, isLoading, noEditGroups, fetchGroups }) => {
  const { t: translate } = useTranslation();
  const firebaseHelper = getFirebaseBackend();
  const { user } = useAuth();
  const accountId = user.account;
  const navigate = useNavigate();
  const [isDeleteModalOpen, setIsDeleteModalOpen] = React.useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = React.useState(false);
  const [selectedGroupName, setSelectedGroupName] = React.useState('');
  const [selectedGroupDefault, setSelectedGroupDefault] = React.useState(false);
  const [selectedGroupId, setSelectedGroupId] = React.useState(null);

  const toggleModal = () => {
    setIsEditModalOpen(!isEditModalOpen);
  };

  const deleteGroup = async (groupId) => {
    const keywords = await firebaseHelper.getKeywordsByGroupId(accountId, groupId);
    const firebaseToken = await firebaseHelper.getIdToken();

    for (const keyword of keywords) {
      await axios.post(
        `${process.env.REACT_APP_API_URL}/deleteKeyword`,
        {
          development: process.env.REACT_APP_BUILD_TYPE !== 'production',
          accountId: accountId,
          groupId: groupId,
          keyword: keyword.name,
        },
        {
          headers: {
            Authorization: `Bearer ${firebaseToken}`,
          },
        },
      );
    }

    await firebaseHelper.deleteGroup(accountId, groupId);
    setIsDeleteModalOpen(false);
    window.location.reload();
  };

  const isOnlyGroupDesagrupado = groups.length === 1 && groups[0].name === 'Desagrupado';

  return (
    <>
      <Suspense fallback={<div>Loading...</div>}>
        {isEditModalOpen && (
          <ModalEditGroup
            isOpen={isEditModalOpen}
            toggle={toggleModal}
            fetchGroups={fetchGroups}
            name={selectedGroupName}
            groupDefault={selectedGroupDefault}
            groupId={selectedGroupId}
          />
        )}
      </Suspense>
      <StyledDeleteModal
        isOpen={isDeleteModalOpen}
        role="dialog"
        keyboard={false}
        autoFocus={true}
        centered={true}
        tabIndex="-1"
      >
        <div className="modal-header">
          <div>{translate('Delete group')}</div>
          <button>
            <i className="bx bx-x" onClick={() => setIsDeleteModalOpen(false)} />
          </button>
        </div>
        <div className="modal-body">
          {translate(
            'By deleting the group, all keywords belonging to it will also be permanently deleted.',
          )}
        </div>
        <div className="modal-footer">
          <button className="cancel-btn" onClick={() => setIsDeleteModalOpen(false)}>
            {translate('Cancel')}
          </button>
          <button className="delete-btn" onClick={() => deleteGroup(selectedGroupId)}>
            {translate('Delete')}
          </button>
        </div>
      </StyledDeleteModal>
      <TableGroupsStyled>
        {isLoading ? (
          <Shimmer height="20em" />
        ) : (
          <Table>
            <thead>
              <tr>
                <th>{translate('My groups')}</th>
                <th style={{ width: '10.5rem' }}>{translate('Keywords')}</th>
                {!noEditGroups && <th style={{ width: '20rem' }}>{translate('Action')}</th>}
              </tr>
            </thead>
            <tbody>
              {isOnlyGroupDesagrupado || groups.length === 0 ? (
                <tr className="w-100">
                  <td colSpan={3}>
                    <div className="d-flex flex-column gap-2 align-items-center justify-content-center p-4">
                      <svg
                        width="89"
                        height="56"
                        viewBox="0 0 89 56"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M12.5156 39.147C4.76773 40.8857 0 43.2543 0 45.8647C0 51.201 19.9233 55.5269 44.5 55.5269C69.0767 55.5269 89 51.201 89 45.8647C89 43.2543 84.2323 40.8857 76.4844 39.147V43.359C76.4844 46.2894 74.6488 48.6911 72.382 48.6911H16.618C14.3513 48.6911 12.5156 46.288 12.5156 43.359V39.147Z"
                          fill="black"
                          fillOpacity="0.06"
                        />
                        <path
                          d="M57.8681 22.3699C57.8681 20.1545 59.2504 18.3256 60.965 18.3242H76.4844V43.3588C76.4844 46.2891 74.6487 48.6909 72.382 48.6909H16.618C14.3512 48.6909 12.5156 46.2878 12.5156 43.3588V18.3242H28.035C29.7496 18.3242 31.1319 20.1504 31.1319 22.3657V22.3961C31.1319 24.6115 32.5295 26.4004 34.2428 26.4004H54.7572C56.4705 26.4004 57.8681 24.5949 57.8681 22.3795V22.3699V22.3699Z"
                          fill="black"
                          fillOpacity="0.04"
                          stroke="black"
                          strokeOpacity="0.15"
                          strokeWidth="0.483696"
                        />
                        <path
                          d="M76.4844 18.4764L62.3751 2.60019C61.6979 1.51803 60.7091 0.86377 59.6675 0.86377H29.3325C28.2909 0.86377 27.3021 1.51803 26.6249 2.59881L12.5156 18.4778"
                          stroke="black"
                          strokeOpacity="0.15"
                          strokeWidth="0.483696"
                        />
                      </svg>
                      <span className="text-no-groups">
                        {groups.length === 0
                          ? translate('No results found')
                          : translate('No groups added')}
                      </span>
                    </div>
                  </td>
                </tr>
              ) : (
                groups
                  .filter((group) => group.name !== 'Desagrupado')
                  .map((group) => (
                    <tr key={group.id}>
                      <td>
                        {group.name}
                        {group.isDefault && <i className="bx bxs-bookmark" />}
                      </td>
                      <td>{group.keywords ? Object.values(group.keywords).length : 0}</td>
                      {!noEditGroups && (
                        <td>
                          <div className="d-flex align-items-center justify-content-center w-100 gap-2">
                            <Button
                              className="btn-manage"
                              onClick={() => {
                                navigate(`/reports/keywords/groups/${group.id}`);
                              }}
                            >
                              {translate('Manage')}
                              <i className="bx bx-right-arrow-alt" />
                            </Button>
                            <Button
                              className="btn-action btn-action-edit"
                              onClick={() => {
                                setSelectedGroupName(group.name);
                                setSelectedGroupDefault(group.isDefault);
                                setSelectedGroupId(group.id);
                                setIsEditModalOpen(true);
                              }}
                            >
                              <i className="bx bx-pencil" />
                            </Button>
                            <Button
                              className="btn-action btn-action-delete"
                              onClick={() => {
                                setSelectedGroupId(group.id);
                                setIsDeleteModalOpen(true);
                              }}
                            >
                              <i className="bx bx-trash" />
                            </Button>
                          </div>
                        </td>
                      )}
                      {/* <td>
                        <div className="group-actions">
                          <Button
                            className="btn-manage"
                            onClick={() => {
                              navigate(`/reports/keywords/groups/${group.id}`);
                            }}
                          >
                            {translate('Manage')}
                            <i className="bx bx-right-arrow-alt" />
                          </Button>
                          <Button className="btn-edit">
                            <i className="bx bx-pencil" /> AAA
                          </Button>
                          <Button className="btn-delete">
                            <i className="bx bx-trash" />
                          </Button>
                        </div>
                      </td> */}
                    </tr>
                  ))
              )}
            </tbody>
          </Table>
        )}
      </TableGroupsStyled>
    </>
  );
};

const StyledDeleteModal = styled(Modal)`
  .modal-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    color: #495057;
    font-size: 1rem;
    font-style: normal;
    font-weight: 600;

    button {
      border: none;
      background-color: transparent;
      margin: 0;
      padding: 0;
      font-size: 1.6875rem;
      color: #484848;
    }
  }

  .modal-body {
    color: #656b7c;
    font-size: 0.8125rem;
    font-style: normal;
    font-weight: 600;
  }

  .modal-footer {
    display: flex;
    justify-content: flex-end;
    align-items: center;
    gap: 1rem;

    button {
      border: none;
      margin: 0;
      padding: 0.5rem 0.87rem;
      border-radius: 0.25rem;
      height: 2.25rem;
      font-size: 0.8125rem;
      font-style: normal;
      font-weight: 600;
    }

    .cancel-btn {
      background-color: #eff2f6;
      color: #000;
    }

    .delete-btn {
      background-color: #f46a6a;
      color: #fff;
    }
  }
`;

TableGroups.propTypes = {
  groups: PropTypes.array.isRequired,
  isLoading: PropTypes.bool.isRequired,
  noEditGroups: PropTypes.bool,
  fetchGroups: PropTypes.func.isRequired,
};

export default TableGroups;
